import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _PropTypes from 'prop-types';
import * as React from 'react';
import { Transition as Transition$1 } from 'react-transition-group';
import { jsx } from 'react/jsx-runtime';

var _excluded = ["duration"],
    _excluded2 = ["transition", "children"];
var slide = {
  entering: {
    style: {
      opacity: 0,
      transform: "scaleY(0.8)"
    },
    transition: {
      duration: 175,
      easing: "ease-out",
      property: "transform, opacity"
    }
  },
  entered: {
    style: {
      opacity: 1,
      transform: "scaleY(1)"
    },
    transition: {
      duration: 175,
      easing: "ease-out",
      property: "transform, opacity"
    }
  },
  exiting: {
    style: {
      opacity: 0
    },
    transition: {
      duration: 100,
      easing: "ease-out",
      property: "transform, opacity"
    }
  }
};
var fade = {
  entering: {
    style: {
      opacity: 0
    },
    transition: {
      duration: 175,
      easing: "ease-in-out",
      property: "opacity"
    }
  },
  entered: {
    style: {
      opacity: 1
    },
    transition: {
      duration: 175,
      easing: "ease-in-out",
      property: "opacity"
    }
  },
  exiting: {
    style: {
      opacity: 0
    },
    transition: {
      duration: 100,
      easing: "ease-in-out",
      property: "opacity"
    }
  }
};
var transitionPresets = {
  slide: slide,
  fade: fade
};
var initialTransitionValues = {
  delay: 0,
  duration: 0,
  property: "all",
  easing: "ease"
};

var buildTransitionValues = function buildTransitionValues(description) {
  var _transitionPropsForma, _transitionPropsForma2;

  var transitionPropsFormatted = Object.entries(description).reduce(function (obj, entry) {
    if (!entry[1]) return obj;

    var _entry = _slicedToArray(entry, 2),
        state = _entry[0],
        definition = _entry[1];

    var _initialTransitionVal = _objectSpread(_objectSpread({}, initialTransitionValues), definition === null || definition === void 0 ? void 0 : definition.transition),
        duration = _initialTransitionVal.duration,
        rest = _objectWithoutProperties(_initialTransitionVal, _excluded);

    var transitionStyles = {
      transitionDelay: "".concat(rest.delay, "ms"),
      transitionDuration: "".concat(duration, "ms"),
      transitionProperty: rest.property,
      transitionTimingFunction: rest.easing
    };
    return _objectSpread(_objectSpread({}, obj), {}, _defineProperty({}, state, {
      style: _objectSpread(_objectSpread({}, definition.style), transitionStyles),
      duration: duration
    }));
  }, {});
  return {
    style: {
      entering: transitionPropsFormatted.entering.style,
      entered: transitionPropsFormatted.entered.style,
      exiting: transitionPropsFormatted.exiting.style,
      exited: (transitionPropsFormatted === null || transitionPropsFormatted === void 0 ? void 0 : (_transitionPropsForma = transitionPropsFormatted.exited) === null || _transitionPropsForma === void 0 ? void 0 : _transitionPropsForma.style) || transitionPropsFormatted.exiting.style,
      unmounted: (transitionPropsFormatted === null || transitionPropsFormatted === void 0 ? void 0 : (_transitionPropsForma2 = transitionPropsFormatted.unmounted) === null || _transitionPropsForma2 === void 0 ? void 0 : _transitionPropsForma2.style) || {}
    },
    duration: {
      appear: transitionPropsFormatted.entering.duration,
      enter: transitionPropsFormatted.entered.duration,
      exit: transitionPropsFormatted.exiting.duration
    }
  };
};

function Transition(props) {
  var _props$transition = props.transition,
      transition = _props$transition === void 0 ? "fade" : _props$transition,
      _children = props.children,
      rest = _objectWithoutProperties(props, _excluded2);

  var nodeRef = React.useRef(null);
  var transitionDescription = typeof transition === "string" ? transitionPresets[transition] : transition;

  var _buildTransitionValue = buildTransitionValues(transitionDescription),
      style = _buildTransitionValue.style,
      duration = _buildTransitionValue.duration;

  return /*#__PURE__*/jsx(Transition$1, _objectSpread(_objectSpread({
    timeout: duration,
    nodeRef: nodeRef
  }, rest), {}, {
    children: function children(state) {
      return _children({
        ref: nodeRef,
        state: state,
        style: style[state]
      });
    }
  }));
}

Transition.propTypes = {
  "children": _PropTypes.func.isRequired,
  "transition": _PropTypes.oneOfType([_PropTypes.oneOf(["fade", "slide"]), _PropTypes.shape({
    "entered": _PropTypes.shape({
      "style": _PropTypes.object.isRequired,
      "transition": _PropTypes.shape({
        "delay": _PropTypes.number,
        "duration": _PropTypes.number,
        "easing": _PropTypes.oneOfType([_PropTypes.oneOf(["-moz-initial", "ease-in-out", "ease-in", "ease-out", "ease", "inherit", "initial", "linear", "revert", "step-end", "step-start", "unset"]), _PropTypes.object]),
        "property": _PropTypes.string
      })
    }).isRequired,
    "entering": _PropTypes.shape({
      "style": _PropTypes.object.isRequired,
      "transition": _PropTypes.shape({
        "delay": _PropTypes.number,
        "duration": _PropTypes.number,
        "easing": _PropTypes.oneOfType([_PropTypes.oneOf(["-moz-initial", "ease-in-out", "ease-in", "ease-out", "ease", "inherit", "initial", "linear", "revert", "step-end", "step-start", "unset"]), _PropTypes.object]),
        "property": _PropTypes.string
      })
    }).isRequired,
    "exited": _PropTypes.shape({
      "style": _PropTypes.object.isRequired,
      "transition": _PropTypes.shape({
        "delay": _PropTypes.number,
        "duration": _PropTypes.number,
        "easing": _PropTypes.oneOfType([_PropTypes.oneOf(["-moz-initial", "ease-in-out", "ease-in", "ease-out", "ease", "inherit", "initial", "linear", "revert", "step-end", "step-start", "unset"]), _PropTypes.object]),
        "property": _PropTypes.string
      })
    }),
    "exiting": _PropTypes.shape({
      "style": _PropTypes.object.isRequired,
      "transition": _PropTypes.shape({
        "delay": _PropTypes.number,
        "duration": _PropTypes.number,
        "easing": _PropTypes.oneOfType([_PropTypes.oneOf(["-moz-initial", "ease-in-out", "ease-in", "ease-out", "ease", "inherit", "initial", "linear", "revert", "step-end", "step-start", "unset"]), _PropTypes.object]),
        "property": _PropTypes.string
      })
    }).isRequired,
    "unmounted": _PropTypes.shape({
      "style": _PropTypes.object.isRequired,
      "transition": _PropTypes.shape({
        "delay": _PropTypes.number,
        "duration": _PropTypes.number,
        "easing": _PropTypes.oneOfType([_PropTypes.oneOf(["-moz-initial", "ease-in-out", "ease-in", "ease-out", "ease", "inherit", "initial", "linear", "revert", "step-end", "step-start", "unset"]), _PropTypes.object]),
        "property": _PropTypes.string
      })
    })
  })])
};
Transition.displayName = "Transition";

export { Transition, transitionPresets };
